import {
  PromotionStatsDay,
  promotionStatsQuery,
} from "@/app/Promotions/GraphQL/promotionStatsQuery";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Currency } from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatStatistic } from "@/lib/Formatters/formatStatistic";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { Bar, BarChart } from "recharts";
import colors from "tailwindcss/colors";
import { useTranslations } from "use-intl";

export const PROMOTION_STAT_DAYS = 7;

type Metric = keyof Omit<PromotionStatsDay, "date">;

const dayFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  day: "numeric",
  month: "short",
});

function formatSpend(value: number) {
  return formatCurrency(value, Currency.Usd, { showZero: true });
}

export function PromotionStatBlocks() {
  const t = useTranslations("promotion.stats");
  const { data } = useSuspenseGqlQuery(promotionStatsQuery, {
    days: PROMOTION_STAT_DAYS,
  });
  const stats = data.promotionStats;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <PromotionStatBlock
        title={t("spend")}
        total={formatSpend(stats.spend_amount)}
        metric="spend_amount"
        days={stats.days}
        color={colors.blue[500]}
        formatValue={formatSpend}
      />
      <PromotionStatBlock
        title={t("impressions")}
        total={formatStatistic(stats.impressions)}
        metric="impressions"
        days={stats.days}
        color={colors.violet[500]}
        formatValue={formatStatistic}
      />
      <PromotionStatBlock
        title={t("clicks")}
        total={formatStatistic(stats.clicks)}
        metric="clicks"
        days={stats.days}
        color={colors.teal[500]}
        formatValue={formatStatistic}
      />
      <PromotionStatBlock
        title={t("conversions")}
        total={formatStatistic(stats.conversions)}
        metric="conversions"
        days={stats.days}
        color={colors.amber[500]}
        formatValue={formatStatistic}
      />
    </div>
  );
}

function PromotionStatBlock({
  title,
  total,
  metric,
  days,
  color,
  formatValue,
}: {
  title: string;
  total: string;
  metric: Metric;
  days: readonly PromotionStatsDay[];
  color: string;
  formatValue: (value: number) => string;
}) {
  const t = useTranslations("promotion.stats");

  const chartConfig = {
    [metric]: { label: title, color },
  } satisfies ChartConfig;

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-500">
          {title}
        </CardTitle>
        <p className="text-3xl font-bold">{total}</p>
        <p className="text-xs text-gray-400">
          {t("window", { days: PROMOTION_STAT_DAYS })}
        </p>
      </CardHeader>
      <CardContent className="mt-auto min-w-0 pt-2">
        <ChartContainer config={chartConfig} className="h-[64px] w-full">
          <BarChart data={[...days]} margin={{ top: 2 }}>
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) =>
                    dayFormatter.format(new Date(payload[0]?.payload.date))
                  }
                  formatter={(value) => (
                    <span className="text-gray-900">
                      {formatValue(Number(value))}
                    </span>
                  )}
                />
              }
            />
            <Bar dataKey={metric} fill={color} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
